import React, { Component } from "react";
import moment from "moment";
import { arrayOf, bool, instanceOf, objectOf, string, shape } from "prop-types";
import classNames from "classnames";
import isEmpty from "lodash/isEmpty";
import I18n from "pages/_components/I18n";
import DatePicker from "pages/_components/fields/datepicker";
import Select from "pages/forms/_components/_fields/Select";
import { Row, Col } from "react-bootstrap";
import * as schedulerUtils from "util/scheduler";
import * as dateUtils from "util/date";
import * as i18nUtils from "util/i18n";
import { getInteger } from "util/config";
import Radio from "pages/_components/fields/Radio";

const numberByFreq = {
    DAY: getInteger("time.frecuency.max.days", 365),
    WEEK: getInteger("time.frecuency.max.weeks", 42),
    MONTH: getInteger("time.frecuency.max.months", 12),
};

class LapseSubOption extends Component {
    static propTypes = {
        enabledWeekDays: arrayOf(bool).isRequired,
        errors: objectOf(string),
        firstWorkingDate: string.isRequired,
        nonWorkingDays: arrayOf(string).isRequired,
        value: instanceOf(moment).isRequired,
        frequency: string.isRequired,
        field: shape({}).isRequired,
        form: shape({}).isRequired,
    };

    static defaultProps = {
        errors: null,
    };

    componentDidMount() {
        const { form } = this.props;
        form.setFieldValue("lapse.lapse", "");
    }

    excludeDates = () => {
        const { nonWorkingDays } = this.props;

        const excludeDates = [];
        nonWorkingDays.forEach((entry) => {
            excludeDates.push(moment(entry));
        });

        return excludeDates;
    };

    filterDate = (date) => {
        const { enabledWeekDays } = this.props;

        return enabledWeekDays[(date.isoWeekday() % 7) + 1];
    };

    getNeverOption = () => (
        <li className="c-control c-control--radio c-control--has-icon">{this.getOption(schedulerUtils.NEVER)}</li>
    );

    getUntilOption = () => {
        const {
            errors,
            firstWorkingDate,
            value: { date, lapse },
            form,
        } = this.props;
        const maxDays = getInteger("time.frecuency.max.days", 365);

        return (
            <li className={classNames("c-control c-control--radio c-control-block c-control--has-icon", {
                    "has-error": !isEmpty(errors) && lapse === schedulerUtils.UNTIL,
                })}>
                <Row className="items-center">
                    <label htmlFor="untilValue.date" className="visually-hidden" aria-hidden>
                        {i18nUtils.get("scheduler.until.a11y")}
                    </label>
                    <Col md={4}>
                        {this.getOption(schedulerUtils.UNTIL)}
                    </Col>
                    <Col md={8} className="form-group form-group--datepicker">
                        <div className="input-group input-group-datepicker">
                            <DatePicker
                                autoComplete="off"
                                tabIndex={lapse === schedulerUtils.UNTIL ? "0" : "-1"}
                                className="form-control"
                                dateFormat={dateUtils.FRIENDY_DATE_FORMAT(i18nUtils.getLang())}
                                minDate={moment(form?.values?.startsOn) || moment(firstWorkingDate)}
                                maxDate={moment().add(maxDays, "days")}
                                name="untilValue"
                                onKeyDown={(e) => e.preventDefault()}
                                onChange={(newDate) => this.handleChange(schedulerUtils.UNTIL, newDate)}
                                selected={moment(date || moment().format("YYYY-MM-DD"))}
                                id="untilValue.date"
                                showYearDropdown
                                showMonthDropdown
                                onChangeRaw={(e) => e.preventDefault()}
                            />
                        </div>
                    </Col>
                </Row>
            </li>
        );
    };

    getTimesOption = () => {
        const {
            errors,
            value: { lapse, number },
            form: {
                values: { frequency },
            },
        } = this.props;

        const options = Array.from({ length: numberByFreq[frequency] }, (_, index) => ({
            id: `${index + 1}`,
            label: `${index + 1} ${i18nUtils.get("scheduler.lapseTimes")}`,
        }));
        return (
            <li
                className={classNames("c-control c-control--radio c-control-block c-control--has-icon", {
                    "has-error": !isEmpty(errors) && lapse === schedulerUtils.UP_TO,
                })}>
                <Row className="items-center">
                    <Col md={4}>
                        {this.getOption(schedulerUtils.UP_TO)}
                    </Col>
                    <Col md={8}>     
                        <div
                            className="form-group form-group--select"
                            style={{ flexDirection: "inherit", justifyContent: "space-between" }}>
                            <label htmlFor="untilValue.date" className="visually-hidden" aria-hidden>
                                {i18nUtils.get("scheduler.up_to.a11y")}
                            </label>
                            <div className="input-group" style={{ flex: 3 }}>
                                <Select
                                    tabIndex={lapse === schedulerUtils.UP_TO ? "0" : "-1"}
                                    className="slideFromBottom"
                                    clearable={false}
                                    labelKey="label"
                                    onChange={(option) => this.handleChange(schedulerUtils.UP_TO, option)}
                                    optionClassName="needsclick"
                                    options={options}
                                    searchable={false}
                                    value={number || 1}
                                    valueKey="id"
                                    id="occurrencies"
                                />
                            </div>
                        </div>
                    </Col>
                </Row>
               
            </li>
        );
    };

    getOption = (id) => {
        const { field } = this.props;

        return (
            <>
                <Radio
                    inLineControl
                    id={id.toLowerCase()}
                    value={id}
                    name="lapseOptions"
                    checked={field.value.lapse === id}
                    onChange={() => this.handleChange(id)}
                    label={
                        <>
                            {id === schedulerUtils.NEVER && <I18n id="scheduler.never" />}
                            {id === schedulerUtils.UNTIL && (
                                <>
                                    <span style={{ whiteSpace: "nowrap" }}>{i18nUtils.get("scheduler.on")}</span>
                                    <label className="visually-hidden" htmlFor="scheduler.on">
                                        {i18nUtils.get("scheduler.until.a11y")}
                                    </label>
                                </>
                            )}
                            {id === schedulerUtils.UP_TO && (
                                <>
                                    <span style={{ whiteSpace: "nowrap" }}>{i18nUtils.get("scheduler.after")}</span>
                                    <label className="visually-hidden" htmlFor="scheduler.after">
                                        {i18nUtils.get("scheduler.up_to.a11y")}
                                    </label>
                                </>
                            )}
                        </>
                    }
                />
            </>
        );
    };

    handleChange = (id, data) => {
        const { value, form, field, firstWorkingDate } = this.props;
        let newValue = null;
        if (id === schedulerUtils.NEVER) {
            newValue = { ...value, lapse: id };
        } else if (id === schedulerUtils.UNTIL) {
            if (firstWorkingDate && typeof data !== "undefined" && data.diff(moment(firstWorkingDate)) < 0) {
                return;
            }
            newValue = { ...value, date: data || form?.values?.startsOn, lapse: id };
        } else if (id === schedulerUtils.UP_TO) {
            newValue = { ...value, lapse: id, number: data ? Number(data.id) : 1 };
        }
        form.setFieldValue(field.name, newValue);
        form.setTouched({ ...form.touched, [field.name]: true });
    };

    render() {
        const { frequency } = this.props;
        return (
            <fieldset id="endRulesFieldsetID">
                <legend className="font-weight-600">
                    <I18n id="scheduler.ends" />
                </legend>
                <div className="form-group">
                    <ul className="form-check-group">
                        {this.getNeverOption()}
                        {frequency !== schedulerUtils.YEAR && this.getUntilOption()}
                        {frequency !== schedulerUtils.CUSTOM && this.getTimesOption()}
                    </ul>
                </div>
            </fieldset>
        );
    }
}

export default LapseSubOption;
